import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Translate } from 'react-localize-redux';
import { DiagnosisTableRow } from '../types';

interface DiagnosisRowsTableProps {
  rows: DiagnosisTableRow[];
  /** Inpatient only. One optional column, so a boolean rather than a column-descriptor list. */
  showDischarges?: boolean;
}

/** A diagnosis seen across a whole hospital would otherwise wrap the row. */
const MAX_SERVICES = 3;

/**
 * SHER-583. The table both ICD-11 diagnosis panels render: Diagnosis, ICD-11 code, Female, Male,
 * Total patients, Main services — plus Discharges for the inpatient one.
 *
 * It exists because the client asked for the two panels to stop looking like different tables,
 * and the honest way to guarantee that is one component rather than two that agree today. It
 * renders rows it is handed and reads nothing from the store, so it mounts in a story or a test
 * with no Redux provider: the Female/Male split is resolved in the data layer, where the rows are
 * assembled and the decrypted patients already are. The
 * labels come from one translation namespace for the same reason: two namespaces holding
 * byte-identical strings drift the first time somebody edits one of them.
 *
 * The empty state is deliberately NOT here. The two panels answer differently — the outpatient
 * one says nothing at all, the inpatient one explains that the ICD-11 field may simply not be
 * configured on that tenant's discharge form — so each call site keeps its own.
 */
const DiagnosisRowsTable: React.FC<DiagnosisRowsTableProps> = ({
  rows,
  showDischarges = false,
}) => (
  <TableContainer component={Paper}>
    {/* size="small" matches every other analytics table. Without it this panel's rows stood
        taller than the rest of the page, which is what the client reported on 2026-09-18. */}
    <Table size="small">
      {/* Every column reads left, counted ones included. Right-aligning the figures is the
          typographic default for numbers, but the client reviewed both panels on 2026-09-23 and
          asked for one edge: the counts are single digits here, so right alignment pushed them
          away from their own headers instead of stacking them for comparison. Kept in the shared
          component rather than at either call site — the two panels differ only in title,
          subtitle and empty state, and alignment is not one of those. */}
      <TableHead>
        <TableRow>
          <TableCell><strong><Translate id="hospital.analytics.diagnosis_table.diagnosis" /></strong></TableCell>
          <TableCell><strong><Translate id="hospital.analytics.diagnosis_table.code" /></strong></TableCell>
          <TableCell><strong><Translate id="hospital.analytics.diagnosis_table.female" /></strong></TableCell>
          <TableCell><strong><Translate id="hospital.analytics.diagnosis_table.male" /></strong></TableCell>
          <TableCell><strong><Translate id="hospital.analytics.diagnosis_table.total" /></strong></TableCell>
          <TableCell><strong><Translate id="hospital.analytics.diagnosis_table.services" /></strong></TableCell>
          {showDischarges && (
            <TableCell><strong><Translate id="hospital.analytics.diagnosis_table.discharges" /></strong></TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.code || row.name}>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.code}</TableCell>
            <TableCell>{row.female}</TableCell>
            <TableCell>{row.male}</TableCell>
            {/* From the API, never derived from the split: the split is counted against a store
                that decrypts progressively, so deriving the total would understate it. A dash
                where the API sent nothing — a zero there would assert something it never said. */}
            <TableCell>{row.patients ?? '—'}</TableCell>
            {/* Ordered by how many of this diagnosis each service saw, not alphabetically,
                so "main services" means what it says. */}
            <TableCell>{row.departments.slice(0, MAX_SERVICES).join(', ')}</TableCell>
            {showDischarges && <TableCell>{row.discharges ?? 0}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export default DiagnosisRowsTable;
